"use client";
import { composeRefs } from "@radix-ui/react-compose-refs";
import { forwardRef, useRef } from "react";
import { twMerge } from "tailwind-merge";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, children, error, ...props }, forwardedRef) => {
    const inputRef = useRef<HTMLInputElement>(null);
    return (
      <div
        className={twMerge(
          "border-grey-300 ring-grey-200/70 has-[input:focus]:border-grey-300 shadow-xs flex items-stretch overflow-hidden rounded-lg border bg-white transition-all has-[input:focus]:ring-3",
          error === true &&
            "border-error-300 has-[input:focus]:border-error-300 ring-error-200/70",
          className,
        )}
        onPointerDown={(event) => {
          // eslint-disable-next-line no-secrets/no-secrets
          // Select the input when clicking on the wrapper if nearby slots don't have focus elements, based on https://github.com/radix-ui/themes/blob/084e8cb81401a00a9d4ad8574b8ae6cfc4fdd937/packages/radix-ui-themes/src/components/text-field.tsx#L55
          const target = event.target as HTMLElement;
          if (target.closest("input, button, a")) {
            return;
          }

          const input = inputRef.current;
          if (!input) {
            return;
          }

          // Same selector as in the CSS to find the right slot
          const isRightSlot = target.closest(`
            .InputSlot[data-side='right'],
            .InputSlot:not([data-side='right']) ~ .InputSlot:not([data-side='left'])
          `);

          const cursorPosition = isRightSlot ? input.value.length : 0;

          requestAnimationFrame(() => {
            try {
              input.setSelectionRange(cursorPosition, cursorPosition);
              // eslint-disable-next-line no-empty
            } catch {}
            input.focus();
          });
        }}
      >
        <input
          className="text-grey-900 placeholder:text-grey-400 flex-1 bg-transparent p-3 py-2 outline-hidden placeholder:font-normal disabled:cursor-not-allowed has-[~_.InputSlot:not([data-side='right'])]:pl-0"
          {...props}
          ref={composeRefs(inputRef, forwardedRef)}
        />
        {children}
      </div>
    );
  },
);

Input.displayName = "Input";

export interface InputSlotProps
  extends React.InputHTMLAttributes<HTMLDivElement> {
  side?: "left" | "right";
}

export const InputSlot = forwardRef<HTMLDivElement, InputSlotProps>(
  ({ className, side, ...props }, ref) => {
    return (
      <div
        data-side={side}
        {...props}
        ref={ref}
        className={twMerge(
          "InputSlot",
          "box-border flex shrink-0 items-center justify-center px-2",
          "[&:not([data-side='right'])]:-order-1 [&:not([data-side='right'])]:pl-3",
          "[&:where([data-side='right']),:where(:not([data-side='right']))_~_:where(:not([data-side='left']))]:order-none",
          "[&:where([data-side='right'])]:pr-3",
          className,
        )}
      />
    );
  },
);

InputSlot.displayName = "Input.Slot";
